import { useState } from 'react';
import type { FieldInputAction } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TemplateFieldControl } from '@/pages/details/template-chrome/TemplateFieldControl';
import {
  EMPTY_CONTROL_STATE,
  resolveControlValue,
  type FieldControlState,
} from '@/pages/details/template-chrome/field-control-state';
import { useCaptureWrite } from '@/hooks/useCaptureWrite';
import { InlineRetryError } from '@/components/ui/inline-retry-error';
import { Loader2 } from 'lucide-react';

interface FieldInputActionCardProps {
  action: FieldInputAction;
  disabled: boolean;
  onSubmit: (value: string) => Promise<void>;
}

const CHIP_OPTION_LIMIT = 4;

// Everything drawn here came from the server-resolved payload (label, control, options); the
// card only collects a value and hands it to the submit path, which writes through
// templates.setFieldValue and echoes the member's line.
export function FieldInputActionCard({ action, disabled, onSubmit }: FieldInputActionCardProps) {
  const [state, setState] = useState<FieldControlState>(EMPTY_CONTROL_STATE);
  const captureWrite = useCaptureWrite();
  const pending = captureWrite.pending;
  const { payload } = action;

  const submit = (value: string) => {
    if (value.length === 0) return;
    void captureWrite.run({
      label: 'templates.setFieldValue',
      write: () => onSubmit(value),
      onWritten: () => setState(EMPTY_CONTROL_STATE),
    });
  };

  const asChips =
    payload.control === 'select' &&
    payload.allowOther !== true &&
    (payload.options?.length ?? 0) > 0 &&
    (payload.options?.length ?? 0) <= CHIP_OPTION_LIMIT;
  const controlId = `field-input-${payload.sectionCode}-${payload.fieldKey}`;
  const value = resolveControlValue(state);

  return (
    <div data-testid="field-input-action-card" className="rounded-md border bg-background p-2.5">
      <Label htmlFor={controlId} className="text-sm font-medium">
        {payload.label}
      </Label>
      {asChips ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {(payload.options ?? []).map((option) => (
            <Button
              key={option}
              type="button"
              variant="outline"
              size="sm"
              className="max-sm:h-11 max-sm:w-full max-sm:text-base"
              disabled={disabled || pending}
              onClick={() => void submit(option)}
            >
              {option}
            </Button>
          ))}
        </div>
      ) : (
        <div className="mt-2 flex items-start gap-2 max-sm:flex-col max-sm:items-stretch">
          <div className="min-w-0 flex-1">
            <TemplateFieldControl
              id={controlId}
              spec={{
                type: payload.control,
                ...(payload.options !== undefined ? { options: payload.options } : {}),
                ...(payload.allowOther !== undefined ? { allowOther: payload.allowOther } : {}),
              }}
              state={state}
              onChange={(next) => {
                captureWrite.clear();
                setState(next);
              }}
              disabled={disabled || pending}
            />
          </div>
          <Button
            size="sm"
            className="max-sm:h-11 max-sm:w-full"
            disabled={disabled || pending || value.length === 0}
            onClick={() => void submit(value)}
          >
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save
          </Button>
        </div>
      )}
      {captureWrite.error !== null ? (
        <div className="mt-1.5">
          <InlineRetryError message={captureWrite.error} onRetry={captureWrite.retry} disabled={pending} />
        </div>
      ) : null}
    </div>
  );
}
